import { useEffect, useState } from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import { sharedMe } from 'lib/owner-notes/api'
import { useOwnerSessionOptional } from 'lib/owner-notes/auth-bridge'
import { hasDeviceMarker, useDeviceEpoch } from 'lib/owner-notes/declared'
import {
  knownTier,
  memberHref,
  ownShownIdentity,
  tierLabel,
  tierProgress,
  youLine,
  youState,
  type OwnShownIdentity,
  type YouState,
} from 'lib/community/model'
import { toNextTierLine } from 'lib/community/hub'
import type { CommunityPoints } from 'lib/community/types'
import { discussionsHref } from 'lib/community/discussions'
import MemberName from './MemberName'
import OwnPicker from './OwnPicker'
import TierAvatar from './TierAvatar'
import base from './community.module.css'
import styles from './YouCard.module.css'

/** Where the card sits (Member Hub 1e): the community sidebar, the leaderboard's sticky row, and
 *  the avatar menu's identity header (`AccountMenuIdentity`, Member Hub 2a). */
export type YouCardLayout = 'card' | 'strip' | 'menu'

export interface YouCardViewProps {
  state: YouState
  /** The points table (the card's "+N"); the menu layout prints no "+N" and may omit it. */
  points?: CommunityPoints
  layout: YouCardLayout
  /** `publicProfileSettingsHref(profileLink.href)` — where a hidden profile turns its page on. */
  settingsHref: string
  trailingSlash: boolean
  onSignIn: () => void
  /** The reader's own name and photo (`ownShownIdentity(me)`, contract B.3) — drawn only for a
   *  member, as the community shows them; null/absent = the handle. */
  shown?: OwnShownIdentity | null
}

/** The reader's member page, only when DealHunt serves one (7839ca41: a member — claimed with a
 *  public profile; never a link that 404s). */
function memberPage(state: YouState, trailingSlash: boolean): string | null {
  return state.kind === 'member' ? memberHref(state.handle, trailingSlash) : null
}

/**
 * ONE "You" card in three layouts (Member Hub 1e — "same avatar, name, tier and progress line in
 * all three"): avatar with the tier ring, "<handle> · <Tier>", "N pts · X to <next tier>" and,
 * in the sidebar, the progress bar. The sidebar adds ONE primary act (plus the outlined answer,
 * f8709b73) and a blue "Your member page →"; the strip adds "Member page →"; the menu's identity
 * row is the link itself. Pure — `YouCard` feeds it the reader's `/me` after hydration.
 */
export const YouCardView: React.VFC<YouCardViewProps> = ({
  state,
  points,
  layout,
  settingsHref,
  trailingSlash,
  onSignIn,
  shown = null,
}) => {
  const [picking, setPicking] = useState(false)
  const standing = state.kind === 'member' || state.kind === 'hidden' ? state.standing : null
  const tier = standing ? knownTier(standing.tier) : 'listener'
  const pts = `${(standing?.points ?? 0).toLocaleString('en-US')} pts`
  const toNext = standing ? toNextTierLine(standing.next_tier) : null
  const page = memberPage(state, trailingSlash)
  // A member who shows their name reads `Name @handle` here as on the boards; every other state
  // keeps the handle (a hidden or unclaimed profile shows no name anywhere, E.4).
  const own = state.kind === 'member' ? shown : null
  const name =
    state.kind === 'listener' ? 'You' : <MemberName name={own?.name} handle={state.handle} />
  // A member's standing says it in figures; every other state needs its sentence (sign in, turn
  // the page on, one review to Contributor).
  const line = state.kind === 'member' && standing ? null : youLine(state)

  const avatar = (
    <TierAvatar
      handle={state.kind === 'listener' ? 'you' : state.handle}
      tier={tier}
      size={layout === 'card' ? 'sm' : 'md'}
      className={styles.avatar}
      src={own?.avatarUrl}
      name={own?.name}
    />
  )

  if (layout === 'menu') {
    // Member Hub 2a / Member Stats v2 boards 2a–2c: handle (or `Name @handle`), "Tier · N pts",
    // "View page →" (only with a page), then the progress bar and "X to <tier>". NO disc
    // (Guillaume 2026-10-11: the header avatar right above it already is the reader's picture).
    // The whole block heads the account menu; the stats glance follows it (`AccountMenuStats`).
    return (
      <div className={styles.you_menu}>
        <div className={styles.who}>
          <span className={styles.name}>
            <b className={styles.title}>{name}</b>
            <span className={styles.points}>{`${tierLabel(tier)} · ${pts}`}</span>
          </span>
          {page && (
            <Linked href={page} className={clsx(styles.pageLink, styles.menuPageLink)}>
              {'View page →'}
            </Linked>
          )}
        </div>
        {standing && (
          <div className={styles.menuProgress}>
            <div className={clsx(styles.bar, styles.menuBar)} aria-hidden="true">
              <span
                className={styles.barFill}
                style={{
                  width: `${tierProgress(
                    standing.points,
                    standing.next_tier?.points_to_go ?? null
                  )}%`,
                }}
              />
            </div>
            {toNext && <span className={styles.points}>{toNext}</span>}
          </div>
        )}
      </div>
    )
  }

  let action: React.ReactNode
  if (state.kind === 'hidden') {
    action = (
      <Linked href={settingsHref} className={base.btn}>
        {'Open Settings'}
      </Linked>
    )
  } else if (state.kind === 'unclaimed') {
    action = (
      <button type="button" className={base.btn} onClick={onSignIn}>
        {'Sign in'}
      </button>
    )
  } else if (picking) {
    action = <OwnPicker id={`you-own-results-${layout}`} autoFocus />
  } else {
    // The review is the act for a listener and for a member, review or not (2026-10-04); the
    // member page is the blue link under it.
    action = (
      <button type="button" className={base.btn} onClick={() => setPicking(true)}>
        {points
          ? `Leave an owner review · +${points.review.toLocaleString('en-US')}`
          : 'Leave an owner review'}
      </button>
    )
  }

  const pageLink = page ? (
    <Linked href={page} className={styles.pageLink}>
      {layout === 'strip' ? 'Member page →' : 'Your member page →'}
    </Linked>
  ) : null

  // Answers score too (2026-10-04: every signed-in profile ranks, owner or not), so the card
  // offers the second way in beside the review — outlined, the review stays the primary. Not for
  // a hidden or unclaimed profile (their step is the switch or the sign-in). `points.reply`
  // absent (older DealHunt) → no figure.
  const answer =
    layout === 'card' && (state.kind === 'listener' || state.kind === 'member') ? (
      <Linked
        href={discussionsHref(trailingSlash, 'unanswered')}
        className={clsx(base.btn, styles.btnSecondary)}
      >
        {points?.reply ? `Answer a discussion · +${points.reply}` : 'Answer a discussion'}
      </Linked>
    ) : null

  return (
    <section
      className={clsx(base.card, base.cardInfo, styles.you, styles[`you_${layout}`])}
      aria-label="Your standing"
    >
      <div className={styles.who}>
        {avatar}
        <div className={styles.name}>
          <p className={styles.title}>
            {name}
            {` · ${tierLabel(tier)}`}
          </p>
          <p className={styles.points}>{toNext ? `${pts} · ${toNext}` : pts}</p>
          {/* The strip's non-member states keep their reason beside the step (sign in, turn the
              page on, one review to Contributor); a member's strip is design 1e's one line. */}
          {layout === 'strip' && line && <p className={styles.points}>{line}</p>}
        </div>
      </div>
      {layout === 'card' && (
        <>
          <div className={styles.bar} aria-hidden="true">
            <span
              className={styles.barFill}
              style={{
                width: `${
                  standing
                    ? tierProgress(standing.points, standing.next_tier?.points_to_go ?? null)
                    : 0
                }%`,
              }}
            />
          </div>
          {line && <p className={base.text}>{line}</p>}
          <div className={styles.ways}>
            {action}
            {answer}
          </div>
          {pageLink}
        </>
      )}
      {/* The strip: a member's one act is their page; anyone else keeps their step. */}
      {layout === 'strip' && (pageLink ?? action)}
    </section>
  )
}

interface YouCardProps {
  points: CommunityPoints
  layout?: YouCardLayout
  /** `publicProfileSettingsHref(profileLink.href)` — where the public-profile switch lives. */
  settingsHref: string
}

/**
 * The reader's own standing. Reader-specific, so it is never in the HTML: the page is
 * shared-cached and this hydrates after it. `/me` is only asked when the browser holds an owner
 * device (`hasDeviceMarker`) — a first-time visitor is a Listener without a request. Everything
 * shown comes from `/me`'s uncached `community` block: right after a publish this card is already
 * right while the pages around it still catch up.
 *
 * Server-rendered host → the session is read through `useOwnerSessionOptional` (Directive
 * 5fd47cfc), never `useOwnerAuth`.
 */
const YouCard: React.VFC<YouCardProps> = ({ points, layout = 'card', settingsHref }) => {
  const { showLogIn } = useOwnerSessionOptional()
  const epoch = useDeviceEpoch()
  const [state, setState] = useState<YouState>({ kind: 'listener' })
  const [shown, setShown] = useState<OwnShownIdentity | null>(null)

  useEffect(() => {
    if (!hasDeviceMarker()) {
      setState({ kind: 'listener' })
      setShown(null)
      return undefined
    }
    let live = true
    sharedMe().then(
      me => {
        if (live) {
          setState(youState(me))
          setShown(ownShownIdentity(me))
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [epoch])

  return (
    <YouCardView
      state={state}
      points={points}
      layout={layout}
      settingsHref={settingsHref}
      trailingSlash={publicConfig('useTrailingSlash')}
      onSignIn={() => void showLogIn()}
      shown={shown}
    />
  )
}

export default YouCard
